import api from "./api";

export interface Projeto {
  id: string;
  nome: string;
  cliente: string;
  clienteId?: string | null;
  interno?: boolean;
  modalidade?: string; // avulso | mensal
  periodicidade?: string | null; // melhoria 26 — rótulo: mensal | trimestral | anual
  etiquetas?: string | null;     // CSV de slugs de ProjetoTag (tags do projeto)
  arquivado?: boolean;
  arquivadoEm?: string | null;
  tipo: string;
  fotoUrl?: string | null;
  status: string;
  aguardandoCliente?: boolean;
  dataInicio: string;
  prazoEntrega: string | null;
  /** MELHORIA 16 — data combinada com o cliente (só gestão recebe; funcionário nem vê). */
  dataEntregaCliente?: string | null;
  dataAssinatura?: string | null;
  fimContrato?: string | null;
  contratoIndeterminado?: boolean;
  prazoTexto?: string | null;
  tempoDesenvolvimentoDias?: number | null;
  prioridade: string;
  dataPrimeiraEntrega?: string | null;
  prazoFeedback?: string | null;
  feedbackRecebidoEm?: string | null;
  inicioSuporte?: string | null;
  fimSuporte?: string | null;
  valor?: number | null;
  valorRecorrente?: number | null;
  custoExtra?: number | null;
  custoExtraDescricao?: string | null;
  responsavel: { id: string; nome: string; email: string };
  _count?: { tarefas: number };
}

export interface TarefaResumo {
  id: string;
  titulo: string;
  descricao: string | null;
  status: string;
  atribuidoA: string;
  prazo: string;
  prioridade: string;
  etiquetas: string[];
  criadoEm: string;
}

export interface ItemMensalDoProjeto {
  id: string;
  /** MELHORIA 8 — item de contrato aponta pro catálogo unificado (TipoTarefa). */
  tipoTarefaId: string | null;
  quantidade: number;
  formato?: string | null;
  especificacoes?: string | null;
  horasVendidas?: number | null;
  /** JSON string {"junior","pleno","senior"} como vem da API. */
  horasPorNivel?: string | null;
  tipoTarefa: { id: string; slug: string; label: string; ativo: boolean; icone?: string; cor?: string | null } | null;
}

/**
 * Como o projeto aparece nos seletores: **com o nome do cliente junto**.
 *
 * Pedido da Thainá (ago/2026): ela tem vários projetos chamados só "Mensal", um
 * por cliente, e ao buscar não dava pra diferenciar — corria o risco de alocar a
 * tarefa no cliente errado. Por isso ela vinha digitando o cliente no nome do
 * projeto ("Barro mensal") na mão; agora sai automático.
 *
 * NÃO duplica quando o nome já traz o cliente (a maioria hoje é "Nuvyon |
 * Mensal", justamente por causa dessa gambiarra) — senão viraria
 * "Nuvyon | Mensal · Nuvyon". Comparação sem acento e sem caixa.
 *
 * Serve também pra BUSCA: o SearchableSelect filtra pelo texto do rótulo, então
 * digitar o nome do cliente passa a encontrar o projeto.
 */
export function rotuloProjeto(p: { nome: string; cliente?: string | null }): string {
  const cliente = (p.cliente || "").trim();
  if (!cliente) return p.nome;
  const semAcento = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
  if (semAcento(p.nome).includes(semAcento(cliente))) return p.nome;
  return `${p.nome} · ${cliente}`;
}

export interface ProjetoDetalhe extends Projeto {
  descricao: string | null;
  responsavelId: string;
  marcos: Marco[];
  tarefas: TarefaResumo[];
  contrato: Contrato | null;
  itensMensais?: ItemMensalDoProjeto[];
}

// Item de contrato no payload de criação/edição (escolha + quantidade + specs/horas).
export interface ItemMensalPayload {
  tipoTarefaId: string;
  quantidade: number;
  formato?: string | null;
  especificacoes?: string | null;
  horasVendidas?: number | null;
  horasPorNivel?: { junior?: number | null; pleno?: number | null; senior?: number | null } | null;
}

export interface Marco {
  id: string;
  nome: string;
  descricao: string | null;
  dataPrevista: string;
  status: string;
}

export interface Contrato {
  id: string;
  modalidade: string;
  valorTotal: number;
  status: string;
}

export async function listarProjetos(filtros?: {
  status?: string;
  responsavelId?: string;
  page?: number;
  perPage?: number;
  interno?: boolean | "all";
  arquivados?: boolean | "all";
  situacao?: "ativos" | "atrasados" | "aguardando";
}) {
  const params = new URLSearchParams();
  if (filtros?.status) params.append("status", filtros.status);
  if (filtros?.responsavelId) params.append("responsavelId", filtros.responsavelId);
  if (filtros?.page) params.append("page", String(filtros.page));
  if (filtros?.perPage) params.append("perPage", String(filtros.perPage));
  if (filtros?.interno !== undefined) params.append("interno", String(filtros.interno));
  if (filtros?.arquivados !== undefined) params.append("arquivados", String(filtros.arquivados));
  if (filtros?.situacao) params.append("situacao", filtros.situacao);

  const response = await api.get(`/projetos?${params.toString()}`);
  return response.data.data;
}

export async function arquivarProjeto(id: string) {
  const r = await api.post(`/projetos/${id}/arquivar`);
  return r.data.data as Projeto;
}

export async function desarquivarProjeto(id: string) {
  const r = await api.post(`/projetos/${id}/desarquivar`);
  return r.data.data as Projeto;
}

export async function buscarProjeto(id: string) {
  const response = await api.get(`/projetos/${id}`);
  return response.data.data as ProjetoDetalhe;
}

export async function criarProjeto(data: {
  nome: string;
  cliente?: string;
  tipo: string;
  dataInicio: string;
  prazoEntrega: string;
  dataEntregaCliente?: string | null;
  responsavelId: string;
  descricao?: string;
  prioridade?: string;
  interno?: boolean;
  modalidade?: string;
  periodicidade?: string | null;
  etiquetas?: string[]; // slugs das tags de projeto
  itensMensais?: ItemMensalPayload[];
}) {
  const response = await api.post("/projetos", data);
  return response.data.data;
}

export async function atualizarProjeto(
  id: string,
  data: Partial<Omit<Projeto, "etiquetas">> & { modalidade?: string; etiquetas?: string[] | null; itensMensais?: ItemMensalPayload[] },
) {
  const response = await api.patch(`/projetos/${id}`, data);
  return response.data.data;
}

export async function deletarProjeto(id: string) {
  await api.delete(`/projetos/${id}`);
}

export interface MembroProjeto {
  id: string;
  nome: string;
  email: string;
  perfil: string;
  fotoUrl: string | null;
}

export async function listarMembrosProjeto(projetoId: string) {
  const r = await api.get(`/projetos/${projetoId}/membros`);
  return r.data.data as MembroProjeto[];
}

export async function definirMembrosProjeto(projetoId: string, usuarioIds: string[]) {
  const r = await api.put(`/projetos/${projetoId}/membros`, { usuarioIds });
  return r.data.data as MembroProjeto[];
}

export async function uploadFotoProjeto(id: string, file: File): Promise<{ fotoUrl: string | null }> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post(`/projetos/${id}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.data;
}

export async function removerFotoProjeto(id: string) {
  await api.delete(`/projetos/${id}/foto`);
}
